/* eslint-disable @next/next/no-img-element */
import { Image } from '@graphcommerce/image'
import { ProductPageGalleryFragment } from '@graphcommerce/magento-product/components/ProductPageGallery/ProductPageGallery.gql'
import { nonNullable, SidebarGalleryProps, TypeRenderer } from '@graphcommerce/next-ui'
import {
  ButtonBack,
  ButtonNext,
  CarouselContext,
  CarouselProvider,
  Slide,
  Slider,
} from 'pure-react-carousel'
import { useContext, useEffect, useMemo, useState } from 'react'
import arrowLeft from '../../assets/images/arrow-left-outline.svg'
import arrowRight from '../../assets/images/arrow-right-outline.svg'

export type ProductPageGalleryRenderers = TypeRenderer<
  NonNullable<NonNullable<ProductPageGalleryFragment['media_gallery']>[0]>
>

export type ProductPageGalleryProps = ProductPageGalleryFragment &
  Omit<SidebarGalleryProps, 'sidebar' | 'images'> & { isMobile: boolean }

/* ---------------- MAIN SLIDER STATE SYNC ---------------- */
export function CarouselStateRaiser({ onSlideChange }: { onSlideChange: (index: number) => void }) {
  const carouselContext = useContext(CarouselContext)
  useEffect(() => {
    const handler = () => onSlideChange(carouselContext.state.currentSlide)
    carouselContext.subscribe(handler)
    return () => carouselContext.unsubscribe(handler)
  }, [carouselContext, onSlideChange])
  return null
}

/* ---------------- THUMBNAIL SLIDER STATE SYNC ---------------- */
function ThumbnailStateWatcher({ onChange }: { onChange: (slide: number) => void }) {
  const carouselContext = useContext(CarouselContext)
  useEffect(() => {
    const handler = () => onChange(carouselContext.state.currentSlide)
    carouselContext.subscribe(handler)
    return () => carouselContext.unsubscribe(handler)
  }, [carouselContext, onChange])
  return null
}

export function ProductPageGallery(props: ProductPageGalleryProps) {
  const { media_gallery, aspectRatio: [width, height] = [1532, 1678], isMobile } = props
  const [currentSlide, setCurrentSlide] = useState(0)
  const [thumbSlide, setThumbSlide] = useState(0)

  const images = useMemo(
    () =>
      media_gallery
        ?.filter(nonNullable)
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        .map((item) => {
          if (item.__typename === 'ProductImage')
            return { src: item.url ?? '', alt: item.label || undefined, width, height }
          return { src: '', alt: `{${item.__typename} not yet supported}`, width, height }
        })
        .filter((img) => img.src), // remove blank images
    [media_gallery, width, height],
  )

  const visibleSlides = (images?.length ?? 0) > 4 ? 4 : images?.length
  const isFirst = thumbSlide === 0
  const isLast = thumbSlide >= (images?.length ?? 0) - (visibleSlides ?? 0)

  return (
    <div className='flex w-full flex-col items-center justify-center'>
      {/* ---------------- MAIN IMAGE CAROUSEL ---------------- */}
      <div className='my-[20px] w-full md:w-[450px] lg:w-[400px] xl:w-[600px]'>
        <CarouselProvider
          naturalSlideWidth={1}
          naturalSlideHeight={1}
          totalSlides={images?.length ?? 0}
          currentSlide={currentSlide}
          infinite
          className='w-full'
        >
          <Slider className='w-full'>
            <CarouselStateRaiser onSlideChange={setCurrentSlide} />
            {images?.map((image, index) => (
              <Slide key={image.src} index={index}>
                <div className='overflow-hidden rounded-[10px] shadow-md'>
                  <img
                    src={image.src}
                    alt={image.alt}
                    className='aspect-square w-full object-cover'
                  />
                </div>
              </Slide>
            ))}
          </Slider>
        </CarouselProvider>
      </div>

      {/* ---------------- THUMBNAIL CAROUSEL ---------------- */}
      <div className='flex w-full items-center justify-center'>
        <CarouselProvider
          naturalSlideWidth={1}
          naturalSlideHeight={1}
          totalSlides={images?.length ?? 0}
          visibleSlides={visibleSlides}
          infinite={false}
          isIntrinsicHeight
          className='mb-[20px] w-full grid grid-cols-1 md:grid-cols-[5fr_90fr_5fr] gap-[10px]'
        >
          <CarouselContext.Consumer>
            {(carouselContext) => (
              <>
                <ButtonBack
                  onClick={() => {
                    const newIndex = Math.max(thumbSlide - 1, 0)
                    carouselContext.setStoreState({ currentSlide: newIndex })
                    setThumbSlide(newIndex)
                  }}
                  disabled={isFirst}
                  className={`z-10 hidden md:flex items-center justify-center ${
                    isFirst ? 'opacity-40 pointer-events-none' : 'hover:scale-110'
                  }`}
                >
                  <Image src={arrowLeft} alt='Back' layout='fixed' unoptimized height={20} />
                </ButtonBack>

                {/* ↓ Constrain the slider so each slide maxes out at 140px */}
                <div
                  className='overflow-hidden'
                  style={{ maxWidth: `${(images?.length ?? 0) * 150}px` }}
                >
                  <Slider>
                    <ThumbnailStateWatcher onChange={setThumbSlide} />
                    {images?.map((image, index) => (
                      <Slide key={image.src} index={index}>
                        <div
                          className={`mx-[5px] overflow-hidden rounded-[8px] border-[1px] border-light-gray shadow-md ${
                            index === currentSlide ? 'shadow-lg shadow-20-grey' : ''
                          }`}
                          style={{ maxWidth: '140px', maxHeight: '140px' }} // ← hard cap per slide
                          tabIndex={0}
                          onClick={() => setCurrentSlide(index)}
                          onKeyDown={() => setCurrentSlide(index)}
                          role='button'
                        >
                          <img
                            src={image.src}
                            alt={image.alt}
                            className='aspect-square w-full object-cover'
                          />
                        </div>
                      </Slide>
                    ))}
                  </Slider>
                </div>

                <ButtonNext
                  onClick={() => {
                    const maxIndex = (images?.length ?? 0) - (visibleSlides ?? 0)
                    const newIndex = Math.min(thumbSlide + 1, maxIndex)
                    carouselContext.setStoreState({ currentSlide: newIndex })
                    setThumbSlide(newIndex)
                  }}
                  disabled={isLast}
                  className={`z-10 hidden md:flex items-center justify-center ${
                    isLast ? 'opacity-40 pointer-events-none' : 'hover:scale-110'
                  }`}
                >
                  <Image src={arrowRight} alt='Next' layout='fixed' unoptimized height={20} />
                </ButtonNext>
              </>
            )}
          </CarouselContext.Consumer>
        </CarouselProvider>
      </div>
    </div>
  )
}
